import axios from 'axios'
import { create } from 'zustand'

import { getDeploymentContract } from '../config/activeDeploymentContract'
import { applyPortalSeo } from './portalSeo'

interface PortalTheme {
  portalName: string
  portalDescription: string | null
  primaryColor: string
  secondaryColor: string
  accentColor: string
  sidebarColor: string
  sidebarTextColor: string
  topBarColor: string
  textColor: string
  backgroundColor: string
  borderColor: string
  logoUrl: string | null
  faviconUrl: string | null
  backgroundImageUrl: string | null
  themeConfig: {
    borderRadius: string
    fontSize: string
    fontFamily: string
    spacing: string
  }
  featuresEnabled: {
    userRegistration: boolean
    passwordReset: boolean
    dashboard: boolean
    marketplace: boolean
    instances: boolean
    monitoring: boolean
    backups: boolean
    networking: boolean
    gateway: boolean
    storage: boolean
    analytics: boolean
    team: boolean
    billing: boolean
    support: boolean
    notifications: boolean
  }
  seoTitle: string | null
  seoDescription: string | null
  privacyPolicyUrl: string | null
  termsOfServiceUrl: string | null
}

interface PortalStore {
  theme: PortalTheme | null
  isPortalDomain: boolean
  isLoading: boolean
  fetchTheme: () => Promise<void>
}

function applyThemeCSS(theme: PortalTheme) {
  const root = document.documentElement.style
  root.setProperty('--portal-primary', theme.primaryColor)
  root.setProperty('--portal-secondary', theme.secondaryColor)
  root.setProperty('--portal-accent', theme.accentColor)
  root.setProperty('--portal-sidebar', theme.sidebarColor)
  root.setProperty('--portal-sidebar-text', theme.sidebarTextColor)
  root.setProperty('--portal-top-bar', theme.topBarColor)
  root.setProperty('--portal-text', theme.textColor)
  root.setProperty('--portal-background', theme.backgroundColor)
  root.setProperty('--portal-border', theme.borderColor)
  root.setProperty('--portal-border-radius', theme.themeConfig.borderRadius)
  root.setProperty('--portal-font-size', theme.themeConfig.fontSize)
  root.setProperty('--portal-font-family', theme.themeConfig.fontFamily)
  root.setProperty('--portal-spacing', theme.themeConfig.spacing)
}

function applyFavicon(url: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = url
}

const THEME_CACHE_KEY = `nebvla-portal-theme-${window.location.hostname}`

function saveThemeCache(theme: PortalTheme) {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(theme))
  } catch {
    /* quota exceeded, ignore */
  }
}

function loadThemeCache(): PortalTheme | null {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY)
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

function clearThemeCache() {
  try {
    localStorage.removeItem(THEME_CACHE_KEY)
  } catch {
    /* ignore */
  }
}

// Restaurar tema do cache sincronamente na inicialização (antes do React renderizar)
const hostname = window.location.hostname
const isPlainLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
const cachedTheme = !isPlainLocalhost ? loadThemeCache() : null

if (cachedTheme) {
  applyThemeCSS(cachedTheme)
}

export const usePortalStore = create<PortalStore>((set) => ({
  theme: cachedTheme,
  isPortalDomain: !!cachedTheme,
  isLoading: !cachedTheme && !isPlainLocalhost,

  fetchTheme: async () => {
    if (isPlainLocalhost) {
      set({ isPortalDomain: false, isLoading: false })
      return
    }

    // Se já tem cache, não mostrar loading (já foi aplicado)
    const hasCache = !!loadThemeCache()
    if (!hasCache) {
      set({ isLoading: true })
    }

    try {
      // A API é a do CONTRATO DE DEPLOY (#996) — a MESMA de `services/api.ts`, e
      // conferida no build. Aqui havia uma segunda cópia da resolução, com
      // `https://api.nexxuscloud.com.br` como fallback: num bundle de staging o tema do
      // portal era buscado em PRODUÇÃO, e o white-label de outro ambiente aparecia.
      // O tema não passa pelo `apiClient` de propósito: ele é buscado ANTES do login,
      // sem interceptor de auth, e um 401 aqui não pode deslogar ninguém.
      const apiBase = getDeploymentContract().apiBaseUrl

      const response = await axios.get(`${apiBase}/api/v1/portal-config/theme`, {
        params: { domain: hostname },
        timeout: 10000,
      })

      const data = response.data
      if (data.portal_name === 'Nebvla') {
        // Backend retornou default — portal não encontrado
        clearThemeCache()
        set({ theme: null, isPortalDomain: false, isLoading: false })
        return
      }

      const themeConfig = data.theme_config || {}
      const features = data.features_enabled || {}

      const theme: PortalTheme = {
        portalName: data.portal_name,
        portalDescription: data.portal_description || null,
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        accentColor: data.accent_color,
        sidebarColor: data.sidebar_color || '#111827',
        sidebarTextColor: data.sidebar_text_color || '#E5E7EB',
        topBarColor: data.top_bar_color || '#FFFFFF',
        textColor: data.text_color || '#111827',
        backgroundColor: data.background_color || '#F9FAFB',
        borderColor: data.border_color || '#E5E7EB',
        logoUrl: data.logo_url || null,
        faviconUrl: data.favicon_url || null,
        backgroundImageUrl: data.background_image_url || null,
        themeConfig: {
          borderRadius: themeConfig.borderRadius || themeConfig.border_radius || '8px',
          fontSize: themeConfig.fontSize || themeConfig.font_size || '14px',
          fontFamily: themeConfig.fontFamily || themeConfig.font_family || 'Inter, sans-serif',
          spacing: themeConfig.spacing || '16px',
        },
        featuresEnabled: {
          userRegistration: features.userRegistration ?? features.user_registration ?? true,
          passwordReset: features.passwordReset ?? features.password_reset ?? true,
          dashboard: features.dashboard ?? true,
          marketplace: features.marketplace ?? true,
          instances: features.instances ?? true,
          monitoring: features.monitoring ?? true,
          backups: features.backups ?? true,
          networking: features.networking ?? true,
          gateway: features.gateway ?? true,
          storage: features.storage ?? true,
          analytics: features.analytics ?? true,
          team: features.team ?? true,
          billing: features.billing ?? true,
          support: features.support ?? true,
          notifications: features.notifications ?? true,
        },
        seoTitle: data.seo_title || null,
        seoDescription: data.seo_description || null,
        privacyPolicyUrl: data.privacy_policy_url || null,
        termsOfServiceUrl: data.terms_of_service_url || null,
      }

      // Aplicar CSS variables e salvar no cache
      applyThemeCSS(theme)
      saveThemeCache(theme)

      // Aplicar SEO: título + meta description + Open Graph, com fallback simétrico
      // (título → portal_name, descrição → portal_description) — ACH-005.
      applyPortalSeo(theme)

      // Aplicar favicon
      if (theme.faviconUrl) {
        applyFavicon(theme.faviconUrl)
      }

      set({ theme, isPortalDomain: true, isLoading: false })
    } catch (error) {
      console.error('[Portal] Falha ao carregar tema:', error)
      // Se não tem cache, limpar estado; se tem cache, manter o que já está aplicado
      if (!hasCache) {
        set({ theme: null, isPortalDomain: false, isLoading: false })
      } else {
        set({ isLoading: false })
      }
    }
  },
}))
